import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Spin, Empty, Tooltip } from "antd";
import {
  SearchOutlined,
  BarChartOutlined,
  FileTextOutlined,
  TableOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Heading from "../../../../components/Heading";
import { ReportConfigService } from "../../../../services/api/reportConfig";
import "./index.scss";

// ── Card icon resolver ────────────────────────────────────────────────────────
const TYPE_ICONS = {
  bar: BarChartOutlined,
  table: TableOutlined,
  text: FileTextOutlined,
};

function ReportCard({ config, onClick }) {
  const Icon = TYPE_ICONS[config.reportType] || BarChartOutlined;
  const pageIcon = config.pageConfig?.icon || null;

  return (
    <div className="drl-card" onClick={() => onClick(config.reportKey)}>
      <div className="drl-card__icon">
        {pageIcon ? (
          <span className="drl-card__emoji">{pageIcon}</span>
        ) : (
          <Icon className="drl-card__anticon" />
        )}
      </div>

      <div className="drl-card__body">
        <div className="drl-card__title">{config.displayName || config.reportKey}</div>

        {config.description && (
          <div className="drl-card__desc">{config.description}</div>
        )}

        <div className="drl-card__meta">
          {config.pageConfig?.downloadable && (
            <Tooltip title="CSV Download available">
              <DownloadOutlined style={{ fontSize: 12, color: "#52c41a" }} />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="drl-card__arrow">›</div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const DynamicReportsList = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const PAGE_SIZE = 100; // load all at once for search UX

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const res = await ReportConfigService.getAll(0, PAGE_SIZE);
      // filter active reports that are explicitly marked to be shown in the list
      const active = (res.data || []).filter(
        (r) => r.isActive !== false && r.pageConfig?.showInList !== false
      );
      setConfigs(active);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return configs;
    return configs.filter(
      (c) =>
        (c.displayName || c.reportKey || "").toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q)
    );
  }, [configs, search]);

  const handleNavigate = (reportKey) => {
    navigate(`/dynamic-report/${reportKey}`);
  };

  return (
    <div className="drl-page">
      {/* Header */}
      <div className="drl-header-wrap">
        <Heading title="⚡ API Reports" navigateTo="/reports">
          <Tooltip title="Refresh list">
            <ReloadOutlined
              className="drl-refresh"
              onClick={loadConfigs}
              spin={loading}
            />
          </Tooltip>
        </Heading>
      </div>

      {/* Search + count */}
      <div className="drl-toolbar">
        <Input
          prefix={<SearchOutlined style={{ color: "#bbb" }} />}
          placeholder="Search reports…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          className="drl-search"
        />
        {!loading && (
          <span className="drl-count">
            {filtered.length} report{filtered.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Cards grid */}
      {loading ? (
        <div className="drl-spin-wrap">
          <Spin size="large" tip="Loading reports…" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="drl-empty">
          <Empty
            description={
              search
                ? `No reports matching "${search}"`
                : "No active reports found. Create one in Report Config Manager."
            }
          />
        </div>
      ) : (
        <div className="drl-grid">
          {filtered.map((config) => (
            <ReportCard
              key={config.reportKey}
              config={config}
              onClick={handleNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DynamicReportsList;
